import { useEffect, useMemo, useRef, useState } from "react";
import { ColorAddedPlayer, IPlayerComparisonData } from "../../types/IPlayerComparison";
import { generateRandomUniqueColors, PlotPlayerSiderbarList } from "./ScattrPlotUtils";
import { addUpdateSortListData } from "./addOrUpdateSortList";
import { useAuth } from "../../contexts/AuthContext";
import { useLoading } from "../../contexts/LoadingContext";
import { useNotification } from "../../contexts/NotificationContext";
import SecondaryButton from "../../ui/SecondaryButton";
import { useNavigate } from "react-router-dom";
import Collpase from "../../assets/icons/Collapse.png";
import RemoveIcon from "../../assets/icons/remove.png";
import { useNewPlayerShortlistContext } from "../../contexts/NewPlayerShortlistContext";
import LabelText from "../../ui/LabelText";
import Switch from "../../ui/CustomSwitch";
import CloseIcon from "@mui/icons-material/Close";
import { TrackerGraph } from "./TrackerGraph";
import { IPlayerCardDetail } from "../../types/IPlayerCardDetail";
import { fetchPlayerCardData } from "../../pages/Chemistry/Utils";
import IApiResponse from "../../types/IApiResponse";
import { postData } from "../../services/api";
import apiUrls from "../../config/apiUrls";
import handleApiResponse from "../../utils/handleApiResponse";
import ChevronLeft from "../../assets/icons/ChevronLeft.png";
import PWNotification from "../PWNotification";
import LoadingSpinner from "../../ui/LoadingSpinner";

interface TrackerComparisonProps {
  playerComparisonData: IPlayerComparisonData[];
  isFormSortList: boolean;
}

export const TrackerComparison: React.FC<TrackerComparisonProps> = ({ playerComparisonData, isFormSortList }) => {
  const { userData } = useAuth();
  const navigate = useNavigate();
  const [selectedSimilarPlayer, setSelectedSimilarPlayer] = useState<ColorAddedPlayer | null>(null);
  const [selectedPlayers, setSelectedPlayers] = useState<number[]>([]);
  const [hiddenPlayers, setHiddenPlayers] = useState<number[]>([]);
  const [isChunkActive, setIsChunkActive] = useState<boolean>(false);
  const [hoveredPlayer, setHoveredPlayer] = useState<number>(0);
  const { loading, startLoading, stopLoading } = useLoading();
  const [isPlayerPosition, setIsPlayerPosition] = useState<boolean>(false);
  const [isPlayerLeague, setIsPlayerLeague] = useState<boolean>(false);
  const [scoreByComparison, setIsScoreByComparison] = useState<boolean>(false);
  const [playerTrackerData, setPlayerTrackerData] = useState<any>(null);
  const { newlyAddedPlayerCount, setNewlyAddedPlayerCount } = useNewPlayerShortlistContext();
  const { showNotification, hideNotification, displayNotification, setDisplayNotification, notificationContent, setNotificationContent } =
    useNotification();
  const [playerCardDetails, setPlayerCardDeatils] = useState<IPlayerCardDetail | undefined>(undefined);
  const [playerCardID, setPlayerCardID] = useState<number>(0);
  const [trackerSelectedPlayer, setTrackerSelectedPlayer] = useState<number[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<{ label: string; value: string }>({
    label: "Attack contribution",
    value: "attackContribution",
  });
  useEffect(() => {
    if (playerCardID > 0) {
      setPlayerCardDeatils(undefined);
      fetchPlayerCardData(startLoading, stopLoading, playerCardID, setPlayerCardDeatils);
    } else {
      setPlayerCardDeatils(undefined);
    }
  }, [playerCardID]);
  const playerComparisonTableData = useMemo(
    () =>
      playerComparisonData?.map((player) => ({
        ...player,
        finishingShots:
          isPlayerLeague && isPlayerPosition
            ? player?.playerFinalThirdLeague?.finishingShotsByPosition
            : isPlayerLeague
            ? player?.playerFinalThirdLeague?.finishingShots
            : isPlayerPosition
            ? player?.playerFinalThirdUniverse?.finishingShotsByPosition
            : player?.playerFinalThirdUniverse?.finishingShots,
        finishingAerial:
          isPlayerLeague && isPlayerPosition
            ? player?.playerFinalThirdLeague?.finishingAerialByPosition
            : isPlayerLeague
            ? player?.playerFinalThirdLeague?.finishingAerial
            : isPlayerPosition
            ? player?.playerFinalThirdUniverse?.finishingAerialByPosition
            : player?.playerFinalThirdUniverse?.finishingAerial,
        attackPositioning:
          isPlayerLeague && isPlayerPosition
            ? player?.playerFinalThirdLeague?.attackPositioningByPosition
            : isPlayerLeague
            ? player?.playerFinalThirdLeague?.attackPositioning
            : isPlayerPosition
            ? player?.playerFinalThirdUniverse?.attackPositioningByPosition
            : player?.playerFinalThirdUniverse?.attackPositioning,
        creativity:
          isPlayerLeague && isPlayerPosition
            ? player?.playerBuildUpPlayLeague?.creativityByPosition
            : isPlayerLeague
            ? player?.playerBuildUpPlayLeague?.creativity
            : isPlayerPosition
            ? player?.playerBuildUpPlayUniverse?.creativityByPosition
            : player?.playerBuildUpPlayUniverse?.creativity,
        passingAccuracy:
          isPlayerLeague && isPlayerPosition
            ? player?.playerBuildUpPlayLeague?.passingAccuracyByPosition
            : isPlayerLeague
            ? player?.playerBuildUpPlayLeague?.passingAccuracy
            : isPlayerPosition
            ? player?.playerBuildUpPlayUniverse?.passingAccuracyByPosition
            : player?.playerBuildUpPlayUniverse?.passingAccuracy,
        dribbling:
          isPlayerLeague && isPlayerPosition
            ? player?.playerBuildUpPlayLeague?.dribblingByPosition
            : isPlayerLeague
            ? player?.playerBuildUpPlayLeague?.dribbling
            : isPlayerPosition
            ? player?.playerBuildUpPlayUniverse?.dribblingByPosition
            : player?.playerBuildUpPlayUniverse?.dribbling,
        crossing:
          isPlayerLeague && isPlayerPosition
            ? player?.playerBuildUpPlayLeague?.crossingByPosition
            : isPlayerLeague
            ? player?.playerBuildUpPlayLeague?.crossing
            : isPlayerPosition
            ? player?.playerBuildUpPlayUniverse?.crossingByPosition
            : player?.playerBuildUpPlayUniverse?.crossing,
        tackling:
          isPlayerLeague && isPlayerPosition
            ? player?.playerDefenceLeague?.tacklingByPosition
            : isPlayerLeague
            ? player?.playerDefenceLeague?.tackling
            : isPlayerPosition
            ? player?.playerDefenceUniverse?.tacklingByPosition
            : player?.playerDefenceUniverse?.tackling,
        defensiveHeaders:
          isPlayerLeague && isPlayerPosition
            ? player?.playerDefenceLeague?.defensiveHeadersByPosition
            : isPlayerLeague
            ? player?.playerDefenceLeague?.defensiveHeaders
            : isPlayerPosition
            ? player?.playerDefenceUniverse?.defensiveHeadersByPosition
            : player?.playerDefenceUniverse?.defensiveHeaders,
        aggression:
          isPlayerLeague && isPlayerPosition
            ? player?.playerDefenceLeague?.aggressionByPosition
            : isPlayerLeague
            ? player?.playerDefenceLeague?.aggression
            : isPlayerPosition
            ? player?.playerDefenceUniverse?.aggressionByPosition
            : player?.playerDefenceUniverse?.aggression,
      })),
    [playerComparisonData, isPlayerLeague, isPlayerPosition]
  );
  const colorsRange = useMemo(() => generateRandomUniqueColors(playerComparisonTableData?.length), [playerComparisonTableData]);
  const [colorsAddedPlayers, setColorsAddedPlayers] = useState(
    playerComparisonTableData?.map((player, i) => ({ ...player, colorCode: colorsRange[i] })) as ColorAddedPlayer[]
  );
  useEffect(
    () =>
      setColorsAddedPlayers(
        playerComparisonTableData?.map((player, i) => ({ ...player, colorCode: colorsRange[i] })) as ColorAddedPlayer[]
      ),
    [playerComparisonTableData]
  );
  const filteredPlayers = useMemo(
    () => colorsAddedPlayers?.filter((player) => !hiddenPlayers?.includes(player?.playerId)),
    [hiddenPlayers, colorsAddedPlayers, playerComparisonTableData]
  );
  const handleRemoveSinglePlayer = (playersToRemove: number) => {
    addUpdateSortListData(userData, [playersToRemove], startLoading, stopLoading, setDisplayNotification, setNotificationContent, true);
    if (hiddenPlayers?.includes(playersToRemove)) {
      setHiddenPlayers((prev) => prev.filter((player) => player !== playersToRemove));
    }
    if (selectedPlayers?.includes(playersToRemove)) {
      setSelectedPlayers((prev) => prev.filter((player) => player !== playersToRemove));
    }
    setColorsAddedPlayers((prevData) => prevData.filter((player) => player?.playerId !== playersToRemove));
  };
  const handleAllSelected = () => {
    setSelectedPlayers(playerComparisonTableData?.map((player) => player?.playerId));
    setHiddenPlayers([]);
  };
  const handleRemovePlayer = (playersToRemove: number[], playerToRemoveFrom: "hidden" | "selected") => {
    addUpdateSortListData(
      userData,
      playersToRemove,
      startLoading,
      stopLoading,
      setDisplayNotification,
      setNotificationContent,
      true,
      navigate
    );
    if (playerToRemoveFrom === "hidden") {
      setHiddenPlayers([]);
    }
    if (playerToRemoveFrom === "selected") {
      setSelectedPlayers([]);
    }
    setColorsAddedPlayers((prevData) => prevData.filter((player) => !playersToRemove.includes(player.playerId)));
  };
  const handleHiddenPlayers = () => {
    setHiddenPlayers((prev) => [...prev, ...selectedPlayers]);
    setSelectedPlayers([]);
  };
  const handlePositionToggle = () => {
    startLoading();
    setIsPlayerPosition((prevState) => !prevState);
    setTimeout(() => stopLoading(), 500);
  };
  const handleLeagueToggle = () => {
    startLoading();
    setIsPlayerLeague((prevState) => !prevState);
    setTimeout(() => stopLoading(), 500);
  };
  const convertToSnakeCase = (str: string) => {
    return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  };
  const fetchPlayerTrackerData = async (playerIDs: number[]) => {
    startLoading();
    const requestParameters = {
      playerId: playerIDs,
      attributeName: convertToSnakeCase(selectedFilter?.value),
    };
    const response: IApiResponse<any> = await postData(apiUrls.PlayerTracker, requestParameters);
    handleApiResponse(
      response,
      (playerTrackerData) => {
        stopLoading();
        setPlayerTrackerData(playerTrackerData);
      },
      (error) => {
        stopLoading();
        console.error("Error fetching player tracker data:", error);
      }
    );
  };
  const prevSelectedCountRef = useRef<number>(0);
  const prevFilterRef = useRef<string>("");
  useEffect(() => {
    if (selectedFilter?.value === "marketValue" || selectedFilter?.value === 'start' || selectedFilter?.value === "wagePerWeek" || selectedFilter?.value === "transferValue") return;
    if (playerComparisonData?.length > 0 && (trackerSelectedPlayer?.length > prevSelectedCountRef.current || selectedFilter.value !== prevFilterRef.current)) {
      fetchPlayerTrackerData(trackerSelectedPlayer);
    }
    prevSelectedCountRef.current = trackerSelectedPlayer?.length || 0;
    prevFilterRef.current = selectedFilter.value;
  }, [playerComparisonData, selectedFilter, trackerSelectedPlayer]);
  useEffect(() => {
    setDisplayNotification(false);
  }, []);
  return (
    <div className="plot-comparison-container">
      <PlotPlayerSiderbarList
        isChunkActive={isChunkActive}
        handleRemoveSinglePlayer={handleRemoveSinglePlayer}
        isFormSortList={isFormSortList}
        setHiddenPlayers={setHiddenPlayers}
        hiddenPlayers={hiddenPlayers}
        selectedSimilarPlayer={selectedSimilarPlayer}
        setSelectedPlayers={setSelectedPlayers}
        selectedPlayers={selectedPlayers}
        playerComparisonTableData={colorsAddedPlayers}
        hoveredPlayer={hoveredPlayer}
        setHoveredPlayer={setHoveredPlayer}
        isNickName={true}
        playerCardDetails={playerCardDetails}
        playerCardID={playerCardID}
        setPlayerCardID={setPlayerCardID}
        setPlayerCardDeatils={setPlayerCardDeatils}
        trackerSelectedPlayer={trackerSelectedPlayer}
        setTrackerSelectedPlayer={setTrackerSelectedPlayer}
        setDisplayNotification={setDisplayNotification}
      />
      {/* {playerTrackerData?.length > 0 && ( */}
        <div className="scattr-plot-container">
          <TrackerGraph
            loading={loading}
            playerCardID={playerCardID}
            setPlayerCardID={setPlayerCardID}
            setHoveredPlayer={setHoveredPlayer}
            hoveredPlayer={hoveredPlayer}
            playerComparisonData={colorsAddedPlayers}
            playerTrackerData={playerTrackerData}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            selectedPlayers={selectedPlayers}
            setSelectedPlayers={setSelectedPlayers}
            trackerSelectedPlayer={trackerSelectedPlayer}
            setTrackerSelectedPlayer={setTrackerSelectedPlayer}
          />
        </div>
      {/* )} */}
      {(!playerTrackerData || playerTrackerData?.length === 0) && (
        <div className="select-notification-parent">
          <div className="select-notification-container">
            <img src={ChevronLeft} alt="left-arrow" />
            Select up to 3 players to track at once.
          </div>
        </div>
      )}
      <div className="plot-comparison-toolbar">
        <div>
          <SecondaryButton iconType="score" onClick={() => setIsScoreByComparison(!scoreByComparison)} isSelected={scoreByComparison} />
          {selectedPlayers?.length !== playerComparisonTableData?.length && <button onClick={() => handleAllSelected()}>Select all</button>}
          {selectedPlayers?.length > 0 && <button onClick={() => setSelectedPlayers([])}>Deselect all</button>}
          {hiddenPlayers?.length > 0 && (
            <button className={`chunk-icon ${isChunkActive && "chunk-active-icon"}`} onClick={() => setIsChunkActive(!isChunkActive)}>
              <img src={Collpase} alt="" />
            </button>
          )}
          {hiddenPlayers?.length > 0 && isFormSortList && (
            <button onClick={() => handleRemovePlayer(hiddenPlayers, "hidden")} className="remove-hidden-players-button">
              <img src={RemoveIcon} height={24} width={24} />
              Remove hidden players
            </button>
          )}
        </div>
        {selectedPlayers?.length > 0 && (
          <div>
            <p>SELECTION OPTIONS:</p>
            <button onClick={() => handleHiddenPlayers()}>Hide selected</button>
            {selectedPlayers?.length !== playerComparisonTableData?.length && (
              <button
                onClick={() =>
                  setHiddenPlayers(
                    playerComparisonTableData
                      ?.filter((player) => !selectedPlayers?.includes(player?.playerId))
                      ?.map((player) => player?.playerId)
                  )
                }
              >
                Hide not selected
              </button>
            )}
            {!isFormSortList && (
              <button
                onClick={() => {
                  setNewlyAddedPlayerCount((prev) => prev + selectedPlayers?.length);
                  addUpdateSortListData(
                    userData,
                    selectedPlayers,
                    startLoading,
                    stopLoading,
                    setDisplayNotification,
                    setNotificationContent,
                    false,
                    navigate
                  );
                }}
              >
                Add to shortlist
              </button>
            )}
            {isFormSortList && (
              <button onClick={() => handleRemovePlayer(selectedPlayers, "selected")} className="remove-hidden-players-button">
                Remove from shortlist
              </button>
            )}
          </div>
        )}
      </div>
      {scoreByComparison && (
        <div className="comparison-by-score-container">
          <div className="comparison-by-score-content">
            <LabelText className="comparison-score-text">Attributes scored in relation to...</LabelText>
            <Switch
              id="position"
              toggled={isPlayerPosition}
              onChange={handlePositionToggle}
              source="Filter"
              title="Player’s position"
              isByComparison={true}
            />
            <Switch
              id="league"
              toggled={isPlayerLeague}
              onChange={handleLeagueToggle}
              source="Filter"
              title="Player’s league"
              isByComparison={true}
            />
          </div>
          <div className="score-notification-closeIcon">
            <CloseIcon sx={{ color: "white" }} onClick={() => setIsScoreByComparison(!scoreByComparison)} />
          </div>
        </div>
      )}
      {displayNotification && (
        <PWNotification
          message={"Max visible players visible on chart. To add other players, turn existing ones off first."}
          setIsNotification={() => setDisplayNotification(false)}
          isDisplayCloseIcon={true}
          className="tracker-selection-notification"
        />
      )}
      {loading && <LoadingSpinner />}
    </div>
  );
};
